// sc/components/RecipesModal.jsx
import React, { useState } from 'react';
import Modal from './Modal';
import { daysLeft } from '../lib/utils';

const API_URL = "https://wltm.fr/api";

function LocalRecipes({ inventory, alertDays }) {
  const thr  = parseInt(alertDays || 7, 10);

  const soon = inventory.filter(i => {
    const d = daysLeft(i.expiry);
    return d !== null && d <= thr;
  });

  const ok = inventory.filter(i => {
    const d = daysLeft(i.expiry);
    return d === null || d > thr;
  });

  const all = [...soon, ...ok];
  const n = all.map(i => i.name.toLowerCase());

  const pool = [];

  if (
    n.some(x => x.includes('pât') || x.includes('spaghetti')) &&
    n.some(x => x.includes('tomate'))
  ) {
    pool.push({
      titre:'Pâtes à la sauce tomate',
      temps:'20 min',
      difficulte:'Facile',
      ingredients_utilises:['Pâtes','Sauce tomate'],
      etapes:[
        "Faire bouillir de l'eau salée.",
        "Cuire les pâtes al dente.",
        "Réchauffer la sauce tomate.",
        "Mélanger et servir."
      ]
    });
  }

  if (n.some(x => x.includes('oeuf') || x.includes('œuf'))) {
    pool.push({
      titre:'Omelette aux herbes',
      temps:'10 min',
      difficulte:'Facile',
      ingredients_utilises:['Œufs'],
      etapes:[
        "Battre les œufs avec sel et poivre.",
        "Chauffer le beurre.",
        "Verser et cuire 3 min.",
        "Replier et servir."
      ]
    });
  }

  if (
    n.some(x =>
      x.includes('viande') ||
      x.includes('steak') ||
      x.includes('poulet') ||
      x.includes('carotte')
    )
  ) {
    pool.push({
      titre:'Poêlée de viande aux légumes',
      temps:'25 min',
      difficulte:'Moyen',
      ingredients_utilises:['Viande','Légumes'],
      etapes:[
        "Émincer les légumes.",
        "Faire revenir la viande 5 min.",
        "Ajouter les légumes 15 min.",
        "Assaisonner et servir."
      ]
    });
  }

  while (pool.length < 3) {
    pool.push({
      titre:'Salade fraîcheur maison',
      temps:'10 min',
      difficulte:'Facile',
      ingredients_utilises: all.slice(0, 3).map(i => i.name),
      etapes:[
        "Rassembler les ingrédients.",
        "Couper et mélanger.",
        "Assaisonner.",
        "Déguster !"
      ]
    });
  }

  return (
    <>
      <div style={{
        background:'rgba(245,158,11,.1)',
        border:'1px solid rgba(245,158,11,.25)',
        borderRadius:11,
        padding:12,
        fontSize:12,
        color:'var(--accent2)',
        marginBottom:14
      }}>
        ⚠️ Mode hors-ligne — recettes locales utilisées.
      </div>

      {pool.slice(0, 3).map((r, i) => (
        <RecipeCard key={i} r={r} />
      ))}
    </>
  );
}

function RecipeCard({ r }) {
  return (
    <div className="rcard">
      <h3>{r.title || r.titre}</h3>

      <div className="rmeta">
        ⏱ {r.time || r.temps}
        {r.difficulte && <> · 📊 {r.difficulte}</>}
      </div>

      <div style={{ marginBottom:8 }}>
        <strong style={{
          fontSize:11,
          color:'var(--muted)',
          textTransform:'uppercase',
          letterSpacing:'.4px'
        }}>
          Ingrédients
        </strong>

        <br />

        {(r.ingredients || r.ingredients_utilises || []).map(x => (
          <span key={x} className="rchip">{x}</span>
        ))}
      </div>

      <div className="rsteps">
        <strong style={{
          fontSize:11,
          color:'var(--muted)',
          textTransform:'uppercase',
          letterSpacing:'.4px'
        }}>
          Préparation
        </strong>

        <br />

        {(r.steps || r.etapes || []).map((e, j) => (
          <span key={j}>
            <b>{j + 1}.</b> {e}
            <br />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RecipesModal({
  open,
  onClose,
  inventory,
  alertDays
}) {
  const [pref, setPref] = useState('');
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState(null);
  const [error, setError] = useState(false);

  const generate = async () => {
    if (!inventory.length) return;

    setLoading(true);
    setRecipes(null);
    setError(false);

    try {
      const thr = parseInt(alertDays || 7, 10);

      const urgentItems = inventory
        .filter(i => {
          const d = daysLeft(i.expiry);
          return typeof d === "number" && d <= thr;
        })
        .map(i => i.name);

      const otherItems = inventory.map(i => i.name);

      const response = await fetch(`${API_URL}/generate-recipes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          urgentItems,
          otherItems,
          preferences: pref
        })
      });

      const data = await response.json();

      console.log("RECIPES:", data);

      if (!response.ok) {
        throw new Error(data.error || "Erreur recettes");
      }

      setRecipes(data);

    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setRecipes(null);
    setError(false);
    setPref('');
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="🍳 Recettes avec ma réserve"
    >
      <p style={{
        color:'var(--muted)',
        fontSize:13,
        marginBottom:14,
        lineHeight:1.6
      }}>
        Recettes générées par IA à partir de votre stock.
        Priorité aux produits qui expirent bientôt.
      </p>

      <div className="fgroup">
        <label className="flabel">
          Préférences / restrictions
        </label>

        <input
          className="finput"
          placeholder="ex: végétarien, rapide, sans gluten…"
          value={pref}
          onChange={e => setPref(e.target.value)}
        />
      </div>

      <button
        className="bsubmit"
        onClick={generate}
        disabled={loading || !inventory.length}
      >
        {loading ? '⏳ Génération…' : '✨ Générer des recettes'}
      </button>

      {loading && (
        <div className="recipe-loading">
          <div className="spin">🍳</div>

          <p style={{
            color:'var(--muted)',
            marginTop:10,
            fontSize:13
          }}>
            Génération des recettes IA…
          </p>
        </div>
      )}

      {error && (
        <LocalRecipes
          inventory={inventory}
          alertDays={alertDays}
        />
      )}

      {recipes && recipes.map((r, i) => (
        <RecipeCard key={i} r={r} />
      ))}

      <button
        className="bcancel"
        onClick={handleClose}
      >
        Fermer
      </button>
    </Modal>
  );
}